/**
 * The CRM surface's modal, extracted on its SECOND real use.
 *
 * `detail-page.tsx` grew a local `ConfirmDialog` and said why it stayed local:
 * "this is the FIRST modal in the CRM surface, and building a general-purpose
 * dialog system for two call sites would be inventing an abstraction before its
 * second real use." That second use arrived — Stacie, 2026-09-03, on log a
 * contact: "maybe when we click log a contact, it pops out so that it's not so
 * overwhelming to keep scrolling within the same thing for a mile" — so the
 * abstraction is now earned rather than anticipated, and both call sites share
 * one set of keyboard and focus rules instead of two.
 *
 * WHAT THE LOCAL VERSION LEFT UNDONE, and this does:
 *
 *  - A FOCUS TRAP. The old comment called it "the obvious next step… noted
 *    rather than half-done". It matters more here than it did there: a confirm
 *    dialog holds two buttons, whereas this one holds a form, and Tab escaping
 *    into the page behind puts the caret in a field the user cannot see.
 *  - FOCUS RESTORE. On close, focus returns to whatever opened the modal. Losing
 *    it to <body> means the next Tab starts from the top of the document.
 *  - A GUARDED DISMISS. Escape and the backdrop close by default, because a
 *    modal that cannot be dismissed feels like a trap. But `confirmDismiss` lets
 *    a caller with typed content ask first — vaporising a call summary because
 *    someone reached for Escape is a worse bug than an extra click.
 *
 * IT PORTALS TO `document.body`, and that is not a nicety — it is the whole
 * reason the dialog is visible at all. The first version of this file listed
 * portals under "deliberately NOT here", reasoning that one modal is open at a
 * time so a portal buys nothing. That was wrong, and Jeff hit it on staging
 * (2026-09-09): the tab pills, the sticky table header and the top bar all
 * painted straight THROUGH an open "Log a contact", clipping the fields behind
 * an opaque bar.
 *
 * The cause is not a z-index that lost a race. `crm-styles.css` pins an
 * expanded row's panel horizontally while a wide table scrolls:
 *
 *   .crm-scope .data-table tbody tr.detail-row > td > * { position: sticky; left: 0 }
 *
 * `position: sticky` creates a stacking context UNCONDITIONALLY, whatever its
 * z-index. `SupplierContactLog` renders inside that panel, so the backdrop's
 * `z-index: 100` was scoped to a container whose own z-index is `auto` — a fact
 * the sticky-header rule already states out loud ("the detail panel, which is
 * itself sticky (left: 0) at z-index auto"). Confined there, 100 never competed
 * with the sticky `th` at 2, the tab triggers at 20, or the top bar at 30. It
 * could not: they are compared one level up, where the whole modal ranks as
 * `auto`.
 *
 * So raising the number would have fixed nothing, and would have hidden the
 * shape of the bug. Any container that creates a stacking context — sticky,
 * transform, filter, opacity, contain, will-change — traps a fixed descendant's
 * z-index the same way, and a shared modal cannot know what it will be rendered
 * inside. The portal is the only fix that holds for callers that do not exist
 * yet.
 *
 * The portal wrapper keeps `className="crm-scope"`. That is load-bearing, not
 * cosmetic: every dialog rule is written `.crm-scope .dialog…`, and the design
 * tokens the dialog's contents read (`--accent`, `--text-*`, `--bg-*`) are
 * DEFINED on `.crm-scope`. Portaled to `document.body` without it, the dialog
 * would leave the selector's reach and render unstyled, with every token
 * resolving to nothing.
 *
 * Still deliberately NOT here: transitions, scroll-lock, a stack. One modal is
 * open at a time in this surface, and each of those has a real cost.
 */
import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/** Everything focusable, in DOM order, for the Tab cycle. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface ModalProps {
  title: string;
  children: React.ReactNode;
  /** Called when the user dismisses — Escape, the backdrop, or the close button. */
  onClose: () => void;
  /**
   * Return false to refuse the dismiss. Called before every dismissal path, so
   * a form with unsaved input can ask once and keep what was typed.
   */
  confirmDismiss?: () => boolean;
  /** Rendered in the action row; the modal supplies no buttons of its own. */
  actions?: React.ReactNode;
  /**
   * 'wide' widens the dialog for a form of twenty-plus fields, so it can lay
   * itself out in two columns instead of becoming a long scroll. Confirmations
   * and short forms keep the default — a wide dialog for three fields reads as
   * an empty page.
   */
  size?: 'default' | 'wide';
  testId: string;
}

export function Modal({
  title,
  children,
  onClose,
  confirmDismiss,
  actions,
  size = 'default',
  testId,
}: ModalProps) {
  const panel = useRef<HTMLDivElement>(null);
  /** Did the mouse press BEGIN on the backdrop? See the dismiss handler. */
  const pressedBackdrop = useRef(false);
  // Captured on mount, before focus moves inside — this is the element that
  // opened us, and where focus has to go back.
  const opener = useRef<HTMLElement | null>(
    typeof document === 'undefined'
      ? null
      : (document.activeElement as HTMLElement | null),
  );

  const dismiss = useCallback(() => {
    if (confirmDismiss && !confirmDismiss()) return;
    onClose();
  }, [confirmDismiss, onClose]);

  // Focus the first thing inside on open, and put focus back on unmount. The
  // restore lives in the same effect as the initial move so the two cannot get
  // out of step.
  useEffect(() => {
    const previous = opener.current;
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();
    return () => {
      // `isConnected` guards the case where the opener itself was unmounted by
      // the very save this modal performed — focusing a detached node throws
      // nothing but silently loses focus to <body>.
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;

      const items = Array.from(
        panel.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) return;

      const first = items[0]!;
      const last = items[items.length - 1]!;
      // Wrap at both ends. Without this, Tab off the last control lands in the
      // page behind the backdrop, where the user cannot see what they typed.
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismiss]);

  return createPortal(
    /*
     * `crm-scope` here, on the portal's own wrapper, because `document.body` is
     * outside the app's — see the header note: the dialog rules and every token
     * it reads are scoped to that class.
     */
    <div className="crm-scope">
      <div
        className="dialog-backdrop"
        data-testid={`${testId}-backdrop`}
        /*
         * DISMISS ONLY WHEN THE PRESS *STARTED* ON THE BACKDROP — both ends, not
         * just the end.
         *
         * The first version tested `e.target === e.currentTarget` alone and its
         * comment claimed that stopped a drag out of a textarea from closing the
         * form. It did not, and Josh caught the overstatement in review
         * (#1949, 2026-09-09).
         *
         * A `click` event's target is the nearest common ANCESTOR of the
         * mousedown and mouseup targets. Press inside the textarea, release on
         * the backdrop, and that ancestor IS the backdrop — so the equality held
         * and the modal dismissed, losing whatever had been typed. Selecting text
         * and overshooting the panel edge is the everyday way to do it.
         *
         * Recording where the press began fixes it for real: the target check
         * still stops a plain click inside the panel from bubbling up here, and
         * `pressedBackdrop` stops the drag.
         */
        onMouseDown={(e) => {
          pressedBackdrop.current = e.target === e.currentTarget;
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && pressedBackdrop.current)
            dismiss();
          pressedBackdrop.current = false;
        }}
      >
        <div
          aria-labelledby={`${testId}-title`}
          aria-modal="true"
          className={size === 'wide' ? 'dialog dialog-wide' : 'dialog'}
          data-testid={testId}
          ref={panel}
          role="dialog"
          tabIndex={-1}
        >
          <h3 className="dialog-title" id={`${testId}-title`}>
            {title}
          </h3>
          <div className="dialog-body">{children}</div>
          {actions && <div className="dialog-actions">{actions}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
